<script setup>
import { ref, onUnmounted, onMounted, nextTick, computed, watch } from 'vue';
import { isHubAdmin } from '../../helpers/liveRole';
import { ROLES } from "../../helpers/constants";
import { useAuthStore } from "../../stores/auth";
import { useSocketStore } from "../../stores/socketStore";
import { saveInteractionData } from "../../helpers/interactionHelper";
import { useSessionStore } from "../../stores/sessionStore";
import axios from 'axios';
import EventResources from "./EventResources.vue";
import Modal from "./shared/modals/ModalComponent.vue";
import SessionDialog from "../../livekit-client/components/SessionDialog.vue";
import XlButton from "../xl/XlButton.vue";
import Session from "../../livekit-client/Session.vue";
import SessionLoadingSkeleton from "../../livekit-client/components/SessionLoadingSkeleton.vue";
import OmniChat from "./OmniChat.vue";
import SessionPanelIcon from "./SessionPanelIcon.vue";

const emit = defineEmits(['countdown-finished']);

const authStore = useAuthStore();
const socketStore = useSocketStore();
const sessionStore = useSessionStore();
const event = ref(null);
const timeRemaining = ref(0);

const interval = ref(null);
const eventResourceType = ref("attendee-popup");
const interactionInterval = ref(null);
const livekitConnected = ref(false);
const isSessionChatOpen = ref(false);
const sessionRef = ref(null);
const pendingSession = ref(null);
const showLeaveSessionModal = ref(false);
const pendingSessionData = ref(null);
const isOmniChatMinimized = ref(false);
// The panel starts on People, for every role and session type.
const activeOmniChatTab = ref('attendees');
const savedSelectedGroup = ref(null);
const omniChatRef = ref(null);

const omniChatNotifications = ref({
  chats: 0,
  qna: 0,
  polls: 0
});

// Not the host, a speaker, the hub owner or hub staff: the owner and staff run
// every session in the hub (helpers/liveRole isHubAdmin).
const isAttendee = computed(() => {
  if (isHubAdmin({ userRole: authStore.userRole, user: authStore.userData })) return false;
  return authStore.userData.subdomain_user_id !== event.value.session_moderator.id &&
    !event.value.session_speakers.some(speaker => speaker.id === authStore.userData.subdomain_user_id);
});

const isModerator = computed(() => {
  return authStore.userData.subdomain_user_id === event.value.session_moderator.id;
});

const sessionChatHeader = computed(() => {
  switch (sessionStore.chatView) {
    case 'polls':
      return 'Polls';
    case 'qanda':
      return 'Q&A';
    case 'attendees':
      return 'People';
    case 'files':
      return 'Files';
    case 'breakouts':
      return 'Rooms';
    // The prototype's header names the section; the conversation is "Chat".
    default:
      return 'Chat';
  }
});

const handleNotificationsUpdated = (counts) => {
  omniChatNotifications.value = counts;
  // The phone's control bar draws the same counts (Session.vue, mobile).
  sessionStore.setPanelUnread(counts);
};

const clearCountdownInterval = () => {
  if (interval.value) {
    clearInterval(interval.value);
    interval.value = null;
  }
};

const clearInteractionInterval = () => {
  if (interactionInterval.value) {
    clearInterval(interactionInterval.value);
    interactionInterval.value = null;
  }
};

const startCountdown = () => {
  clearCountdownInterval();
  const startTime = new Date(event.value.start_date).getTime();
  const endTime = new Date(event.value.end_date).getTime();
  const now = Date.now();

  if (now < startTime) {
    timeRemaining.value = Math.floor((startTime - now) / 1000);
  } else if (now < endTime) {
    timeRemaining.value = Math.floor((endTime - now) / 1000);
  } else {
    timeRemaining.value = 0;
    emit("countdown-finished");
    return;
  }

  interval.value = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      clearInterval(interval.value);
      emit("countdown-finished");
    }
  }, 1000);
};

const handleOpen = async () => {
  eventResourceType.value = isModerator.value ? 'admin-settings' : eventResourceType.value;
  startCountdown();
};

// Every way into a room opens it 300ms later; unmounting inside that gap must
// cancel it, or it starts a countdown nothing is left to clear.
let openTimer = null;
const scheduleOpen = () => {
  clearTimeout(openTimer);
  openTimer = setTimeout(handleOpen, 300);
};

const handleSessionReady = () => {
  livekitConnected.value = true;
};

const handleSessionConnected = () => {
  const user = authStore.userData;
  // Whoever comes straight in gets the chat at once. Not only the listed host
  // and speakers: the room's role decides who runs it — hub staff, the owner,
  // whoever is on the Office Hours desk — and they never wait to be admitted,
  // which is the other way the panel opens (Office Hours: the desk had no chat
  // while the caller did, so what a policyholder typed was never seen).
  if (!isAttendee.value || sessionStore.isAdmin) {
    isSessionChatOpen.value = true;
  }

  socketStore.emit("session_open", {
    ...event.value,
    user_id:  user.subdomain_user_id,
    sso_user_id: user.id,
    current_subdomain: window.store.subdomain,
  });
};

const handleSessionDisconnected = async ({ participantId, remainingParticipants }) => {
  console.log('Session disconnected for participant:', participantId, remainingParticipants);
  isSessionChatOpen.value = false;
  document.body.style.overflow = "";
  const user = authStore.userData;
  if (remainingParticipants === 0) {
    sessionStore.clearActiveSessionId();
    try {
      await axios.put(`${window.store.event_backend_api}/move-to-global/${event.value.id}`);
    } catch (error) {
      console.error(error);
    }
  } else {
    // Session still has participants — prevent video calls on session group chats
    sessionStore.setActiveSessionId(event.value.id);
  }

  socketStore.emit("session_close", {
    ...event.value,
    user_id: user.subdomain_user_id,
    sso_user_id: user.id,
    current_subdomain: window.store.subdomain,
  });

  clearCountdownInterval();
  clearInteractionInterval();

  // Check if there's a pending session to switch to
  if (pendingSession.value) {
    const nextSession = pendingSession.value;
    pendingSession.value = null;

    // Reset state for new session
    livekitConnected.value = false;
    event.value = null;
    sessionStore.reset();

    // Set up interaction interval for the new session
    if (authStore.userData && authStore.userData.role && authStore.userData.role !== ROLES.EVENT_HOST && authStore.userData.role !== ROLES.OWNER) {
      interactionInterval.value = setInterval(() => {
        saveInteractionData(authStore.userData, 'jitsi-session');
      }, 1000 * 60 * 15);
    }

    // Wait for DOM to update, then join the new session
    await nextTick();
    event.value = nextSession;
    scheduleOpen();
  }
};

const handleSessionClose = () => {
  // A Join locks page scroll; closing the room before it ever connects (the
  // pre-join screen, the waiting room) fires no disconnect to unlock it.
  document.body.style.overflow = '';
  window.haveSessionEventStarted = false;
  event.value = null;
  livekitConnected.value = false;
  sessionStore.reset();
  // The room's 15-minute interaction log ends with it (a pending session starts its own).
  clearInteractionInterval();

  // If there's a pending session, join it
  if (pendingSession.value) {
    const nextSession = pendingSession.value;
    pendingSession.value = null;

    // Set up interaction interval for the new session
    if (authStore.userData && authStore.userData.role && authStore.userData.role !== ROLES.EVENT_HOST && authStore.userData.role !== ROLES.OWNER) {
      interactionInterval.value = setInterval(() => {
        saveInteractionData(authStore.userData, 'jitsi-session');
      }, 1000 * 60 * 15);
    }

    nextTick(() => {
      event.value = nextSession;
      scheduleOpen();
    });
  }
};

const handleSessionAdmitted = () => {
  isSessionChatOpen.value = true;
};

// "Just type — no camera or mic" on the pre-join screen: the chat is how they
// take part, so that choice opens on Chat (when the session has one). Office
// Hours has no Chat: People is its only section.
const handleOpenChat = () => {
  isSessionChatOpen.value = true;
  handleExpandOmniChat(officeHoursPanel.value || omniChatRef.value?.sessionChatsVisible === false ? 'attendees' : 'groups');
};

// "See them" on the room's waiting bar: People, where the waiting are listed.
const handleOpenPeople = () => {
  isSessionChatOpen.value = true;
  handleExpandOmniChat('attendees');
};

// What the folded rail offers follows the open one (OmniChat): Chat only when
// the session has chat, Q&A, Polls, Rooms and Files only when the session's settings
// have them on (sessionStore); Office Hours, People only.
const chatOffered = computed(() => omniChatRef.value?.sessionChatsVisible !== false && !officeHoursPanel.value);
const officeHoursPanel = computed(() => event.value?.video_call_type === 'office_hours');
watch(chatOffered, (offered) => sessionStore.setChatOffered(offered), { immediate: true });

// A tool chosen on the phone's control bar or in More: the panel, open on
// that section (Session.vue shows it in a bottom sheet).
const handleOpenPanel = (tab) => {
  isSessionChatOpen.value = true;
  handleExpandOmniChat(tab);
};

// The agenda's way into this room. Taken back on unmount, but only while it is
// still ours: a newer EventChat may have installed its own since.
let ownJoin = null;

onMounted(() => {
  ownJoin = async (eventData) => {
    // Check if we're already in a session
    if (event.value && sessionRef.value) {
      pendingSessionData.value = eventData;
      showLeaveSessionModal.value = true;
      return;
    }

    // No current session, join directly
    if (authStore.userData && authStore.userData.role && authStore.userData.role !== ROLES.EVENT_HOST && authStore.userData.role !== ROLES.OWNER) {
      clearInteractionInterval(); // never two
      interactionInterval.value = setInterval(() => {
        saveInteractionData(authStore.userData, 'jitsi-session');
      }, 1000 * 60 * 15);
    }
    event.value = eventData;
    scheduleOpen();
  };
  window.joinSessionEvent = ownJoin;
});

const handleLeaveAndJoin = async () => {
  showLeaveSessionModal.value = false;
  if (!pendingSessionData.value) return;

  const eventData = pendingSessionData.value;
  pendingSessionData.value = null;

  // Store the new session to switch to after disconnecting
  pendingSession.value = eventData;

  // Disconnect from current session - this will trigger handleSessionDisconnected
  // which will then join the pending session
  try {
    await sessionRef.value.leaveRoom(false);
    sessionStore.reset();
  } catch (e) {
    console.error('Error leaving current session:', e);
    // Even if there's an error, try to join the new session
    pendingSession.value = null;
    livekitConnected.value = false;
    event.value = null;
    sessionStore.reset();
    await nextTick();
    event.value = eventData;
    scheduleOpen();
  }
};

onUnmounted(() => {
  // A join locks page scroll; the disconnect handler that normally unlocks it
  // never runs when this unmounts (Vue drops emits from an unmounted instance),
  // so a nav click out of a live call would leave the next page unscrollable.
  document.body.style.overflow = '';
  clearTimeout(openTimer);
  clearCountdownInterval();
  clearInteractionInterval();
  if (window.joinSessionEvent === ownJoin) delete window.joinSessionEvent;
});

const handleMinimizeOmniChat = () => {
  // Capture current tab and selected group from OmniChat
  if (omniChatRef.value) {
    if (omniChatRef.value.view) {
      activeOmniChatTab.value = omniChatRef.value.view;
    }
    if (omniChatRef.value.selectedGroup) {
      savedSelectedGroup.value = omniChatRef.value.selectedGroup;
    }
  }
  isOmniChatMinimized.value = true;
};

const handleExpandOmniChat = (tab = null) => {
  if (tab) {
    activeOmniChatTab.value = tab;
    // If switching to a specific tab via icon, clear the saved group
    savedSelectedGroup.value = null;
  }
  isOmniChatMinimized.value = false;
  // Restore the state in OmniChat after expanding
  nextTick(() => {
    if (omniChatRef.value) {
      // If we have a saved group and the tab is 'messages', restore the group
      if (savedSelectedGroup.value && activeOmniChatTab.value === 'messages') {
        omniChatRef.value.selectGroup(savedSelectedGroup.value);
      } else if (omniChatRef.value.handleChangeView) {
        omniChatRef.value.handleChangeView(activeOmniChatTab.value);
      }
    }
  });
};
</script>

<template>
  <div class="agenda">

    <div class="modals-wrapper" v-if="event">
       <modal
          v-show="!livekitConnected"
          width="100%"
          height="100%"
          max-width="100%"
          max-height="100%"
          body-background="inherit"
          :header="false"
          :close-header-button="false"
          class="skeleton-modal"
      >
        <SessionLoadingSkeleton />
      </modal>

      <modal
        v-show="livekitConnected"
        width="100%"
        height="100%"
        max-width="100%"
        max-height="100%"
        :header="false"
        :close-header-button="sessionStore.waitingForHost || (!sessionStore.isConnected && isAttendee) || sessionStore.hasLeftRoom"
        class="session-modal"
        @close="handleSessionClose"
      >
        <Session
          ref="sessionRef"
          :event="event"
          @ready="handleSessionReady"
          @connected="handleSessionConnected"
          @disconnected="handleSessionDisconnected"
          @close="handleSessionClose"
          @admitted="handleSessionAdmitted"
          @open-chat="handleOpenChat"
          @open-people="handleOpenPeople"
          @open-panel="handleOpenPanel"
        >
          <!-- The panel sits in the room's own container, beside the stage
               (prototype .rmbody: stage, then .rmside). -->
          <template #panel>
            <template v-if="isSessionChatOpen">
              <!-- The session panel (prototype .rmside): a 320px card; folded, a 74px labelled rail. -->
              <div class="omnichat-sidebar" :class="{ 'omnichat-sidebar--minimized': isOmniChatMinimized }" data-xl-gen="northstar">
                <!-- Minimized content (prototype .rmside.mini) -->
                <div v-show="isOmniChatMinimized" class="omnichat-minimized">
                  <button type="button" class="omnichat-minimized__open" title="Open the panel" aria-label="Open the panel" @click="handleExpandOmniChat()">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9.8 18 6-6-6-6" /></svg>
                  </button>
                  <nav class="omnichat-minimized__icons" aria-label="Session panel sections">
                    <button v-if="chatOffered" type="button" class="omnichat-minimized__icon" :class="{ on: ['groups', 'messages'].includes(activeOmniChatTab) }" title="Chat" @click="handleExpandOmniChat('groups')">
                      <span class="omnichat-minimized__ri">
                        <SessionPanelIcon name="chat" />
                        <i v-if="omniChatNotifications.chats > 0" class="minimized-badge">{{ omniChatNotifications.chats }}</i>
                      </span>
                      <span class="omnichat-minimized__rl">Chat</span>
                    </button>
                    <button v-if="sessionStore.qaEnabled && !sessionStore.isInBreakout && !officeHoursPanel" type="button" class="omnichat-minimized__icon" :class="{ on: activeOmniChatTab === 'qanda' }" title="Q&amp;A" @click="handleExpandOmniChat('qanda')">
                      <span class="omnichat-minimized__ri">
                        <SessionPanelIcon name="qa" />
                        <i v-if="omniChatNotifications.qna > 0" class="minimized-badge">{{ omniChatNotifications.qna }}</i>
                      </span>
                      <span class="omnichat-minimized__rl">Q&amp;A</span>
                    </button>
                    <button v-if="sessionStore.pollsEnabled && !sessionStore.isInBreakout && !officeHoursPanel" type="button" class="omnichat-minimized__icon" :class="{ on: activeOmniChatTab === 'polls' }" title="Polls" @click="handleExpandOmniChat('polls')">
                      <span class="omnichat-minimized__ri">
                        <SessionPanelIcon name="polls" />
                        <i v-if="omniChatNotifications.polls > 0" class="minimized-badge">{{ omniChatNotifications.polls }}</i>
                      </span>
                      <span class="omnichat-minimized__rl">Polls</span>
                    </button>
                    <button v-if="sessionStore.isAdmin && sessionStore.breakoutsEnabled && !officeHoursPanel" type="button" class="omnichat-minimized__icon" :class="{ on: activeOmniChatTab === 'breakouts' }" title="Breakout rooms" @click="handleExpandOmniChat('breakouts')">
                      <span class="omnichat-minimized__ri"><SessionPanelIcon name="rooms" /></span>
                      <span class="omnichat-minimized__rl">Rooms</span>
                    </button>
                    <button v-if="sessionStore.filesEnabled && !sessionStore.isInBreakout && !officeHoursPanel" type="button" class="omnichat-minimized__icon" :class="{ on: activeOmniChatTab === 'files' }" title="Files" @click="handleExpandOmniChat('files')">
                      <span class="omnichat-minimized__ri"><SessionPanelIcon name="files" /></span>
                      <span class="omnichat-minimized__rl">Files</span>
                    </button>
                    <button type="button" class="omnichat-minimized__icon" :class="{ on: activeOmniChatTab === 'attendees' }" title="People" @click="handleExpandOmniChat('attendees')">
                      <span class="omnichat-minimized__ri">
                        <SessionPanelIcon name="people" />
                        <!-- A hand up: a dot, so the host sees it with the panel closed. -->
                        <i
                            v-if="sessionStore.isAdmin && sessionStore.handQueue.length"
                            class="minimized-hand"
                            :title="`${sessionStore.handQueue.length} with a hand up`"
                            data-testid="people-hand-dot"
                        />
                        <i v-if="sessionStore.isAdmin && sessionStore.waitingAttendees.length" class="minimized-badge" data-testid="people-waiting-badge">{{ sessionStore.waitingAttendees.length }}</i>
                      </span>
                      <span class="omnichat-minimized__rl">People</span>
                    </button>
                  </nav>
                </div>

                <!-- Expanded content -->
                <div v-show="!isOmniChatMinimized" class="omnichat-expanded">
                  <!-- The room on its way: the stage's own shape, not a spinner. -->
                  <SessionLoadingSkeleton v-show="!livekitConnected" :with-chat="false" />
                  <div v-show="livekitConnected" class="chat-modal__container">
                    <!-- The section's name, and the one control the header has: fold (prototype .rmhd3). -->
                    <div class="chat-modal__container--header">
                      <h2 data-testid="session-panel-title">{{ sessionChatHeader }}</h2>
                      <button type="button" class="chat-modal__container--header--minimize" title="Hide the panel" aria-label="Hide the panel" @click="handleMinimizeOmniChat">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9.8 18 6-6-6-6" /></svg>
                      </button>
                    </div>
                    <OmniChat 
                      ref="omniChatRef" 
                      in-session 
                      hide-header 
                      :session="event" 
                      :is-moderator="isModerator"
                      @notifications-updated="handleNotificationsUpdated"
                      @minimize="handleMinimizeOmniChat"
                    />
                  </div>
                </div>
              </div>
            </template>
          </template>
        </Session>
      </modal>

    </div>

    <SessionDialog v-if="showLeaveSessionModal" title="Leave current session?" icon="leave" data-testid="leave-and-join-dialog">
      <p>You are currently in a live session. Joining another session will disconnect you from the current one. Do you want to continue?</p>
      <template #actions>
        <XlButton variant="ghost" @click="showLeaveSessionModal = false">Cancel</XlButton>
        <XlButton @click="handleLeaveAndJoin">Leave &amp; Join New Session</XlButton>
      </template>
    </SessionDialog>
  </div>

</template>

<style lang="scss" scoped>
.agenda {
  &__title-middle-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    flex: 1 0 0;

    &__title {
      color: #1E1A1F;
      font-feature-settings: "liga" off, "clig" off;
      font-family: Lato, sans-serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0.15px;
    }

    &__date,
    &__countdown {
      color: #1E1A1F;
      font-family: Lato, sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: 0.15px;
    }
  }

  .modals-wrapper {
    position: fixed;
    top: 63px;
    left: 0;
    width: 100%;
    height: calc(100vh - 63px);
    z-index: 149;
    background-color: rgba(39, 38, 37, 0.45);
    display: flex;
    gap: 12px;
    padding: 12px;
    overflow: hidden;

    /* Override ModalComponent styles */
    :deep(.chat-modal), :deep(.session-modal) {
      position: static;
      width: auto;
      height: 100%;
      background: transparent;
      display: block;
      padding: 0;
    }

    :deep(.skeleton-modal) {
      position: static;
      width: 100%;
      height: 100%;
      background: transparent;
      padding: 0;
    }

    // The room's card (prototype .win): the header, notices, stage and panel
    // all sit inside it.
    :deep(.modal__body) {
      height: 100%;
      margin: 0;
      border-radius: 22px;
      box-shadow: 0 26px 70px rgba(28, 23, 32, 0.22);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    :deep(.modal__content) {
      flex: 1;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .session-modal {
      flex: 1;
      min-width: 0;
    }

    .chat-modal {
      width: 320px;
      flex-shrink: 0;

      &__container {
        display: flex;
        flex-direction: column;
        flex: 1;
        height: 100%;
        min-height: 0;
        overflow: hidden;

        // prototype .rmhd3: 34px | name | 34px, the fold control on the right
        &--header {
          display: grid;
          grid-template-columns: 34px 1fr 34px;
          align-items: center;
          gap: 6px;
          padding: 9px 10px;
          border-bottom: 1px solid var(--xl-color-border, #EDE7F0);
          background: #fff;
          flex-shrink: 0;

          h2 {
            grid-column: 2;
            margin: 0;
            text-align: center;
            font-family: var(--xl-font-family);
            font-size: 14px;
            font-weight: 600;
            letter-spacing: -0.01em;
            color: var(--xl-color-on-surface, #1C1720);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &--minimize {
            grid-column: 3;
            justify-self: end;
            width: 28px;
            height: 28px;
            padding: 0;
            border: 0;
            border-radius: 9px;
            background: transparent;
            color: var(--xl-color-outline, #867D8D);
            display: grid;
            place-items: center;
            cursor: pointer;

            &:hover { background: var(--xl-color-border-subtle, #F6F2F8); color: var(--xl-color-on-surface, #1C1720); }
            &:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }
          }
        }

        // OmniChat wrapper - ensure it takes remaining space
        :deep(.wrap) {
          flex: 1;
          min-height: 0;
          height: 0; // Force flex to control height
          overflow: hidden;
        }
      }
    }
  }


  // prototype .rmside: a white card, hairline border and shadow
  .omnichat-sidebar {
    width: 320px;
    height: 100%;
    flex-shrink: 0;
    transition: width 0.3s ease;
    overflow: hidden;
    background-color: #fff;
    border: 1px solid var(--xl-color-border, #EDE7F0);
    border-radius: 16px;
    box-shadow: var(--xl-shadow-1, 0 1px 2px rgba(28, 23, 32, 0.04));
    font-family: var(--xl-font-family);

    &--minimized {
      width: 74px;
    }
  }

  // prototype .rmside.mini
  .omnichat-minimized {
    display: flex;
    flex-direction: column;
    width: 74px;
    height: 100%;

    &__open {
      flex: 0 0 auto;
      width: 100%;
      height: 42px;
      padding: 0;
      border: 0;
      border-bottom: 1px solid var(--xl-color-border, #EDE7F0);
      background: transparent;
      color: var(--xl-color-outline, #867D8D);
      display: grid;
      place-items: center;
      cursor: pointer;

      svg { transform: rotate(180deg); }
      &:hover { background: var(--xl-color-border-subtle, #F6F2F8); color: var(--xl-color-on-surface, #1C1720); }
      &:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: -2px; }
    }

    &__icons {
      display: flex;
      flex-direction: column;
      padding: 4px 0;
    }

    &__icon {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      width: 100%;
      padding: 10px 2px 9px;
      border: 0;
      background: transparent;
      color: var(--xl-color-outline, #867D8D);
      cursor: pointer;
      transition: color 0.15s;

      &:hover { color: var(--xl-color-on-surface-variant, #514A57); }
      &:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: -2px; }
      &.on { color: var(--xl-color-primary, #5E2F78); }

      // the open section: a 2px bar on the left edge
      &.on::before {
        content: "";
        position: absolute;
        top: 12%;
        bottom: 12%;
        left: 0;
        width: 2px;
        border-radius: 2px;
        background: var(--xl-color-primary, #5E2F78);
      }
    }

    &__ri { position: relative; display: grid; place-items: center; }
    &__rl { display: block; font-size: 9.5px; font-weight: 600; line-height: 1.2; text-align: center; letter-spacing: -0.01em; }

    .minimized-badge {
      position: absolute;
      top: -5px;
      right: -9px;
      min-width: 15px;
      height: 15px;
      padding: 0 3px;
      border-radius: 99px;
      display: grid;
      place-items: center;
      background: var(--xl-color-error, #A32020);
      color: #fff;
      font-size: 9.5px;
      font-weight: 700;
      font-style: normal;
      box-shadow: 0 0 0 2px #fff;
    }

    // A hand up (prototype .knockdot): an amber dot on the icon's other corner,
    // clear of the count.
    .minimized-hand {
      position: absolute;
      top: -3px;
      left: -7px;
      width: 8px;
      height: 8px;
      border: 1.5px solid #fff;
      border-radius: 50%;
      background: var(--xl-color-warning, #8A5200);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .omnichat-sidebar, .omnichat-minimized__icon { transition: none; }
  }

  // A phone: the room is the whole screen (Mobile in-session; prototype
  // XtendLive_Mobile_Room.html) — no frame, no gutter, no hub bar above it.
  @media (max-width: 600px), (pointer: coarse) and (max-width: 1024px) {
    .modals-wrapper {
      top: 0;
      height: 100vh;
      height: 100dvh;
      padding: 0;

      :deep(.modal__body) { border-radius: 0; box-shadow: none; }
    }
  }

  .omnichat-expanded {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
}
</style>
